import React from 'react';
import { useStore } from 'effector-react';
import { getExt, randomId, removeExt } from 'pano-utils';
import { useImmer } from 'use-immer';
import { useMountedState } from 'react-use';
import { nanoid } from 'nanoid';
import {
  LibApiType,
  LibType,
  type LibUploadingMap,
  QiniuTokenType,
} from '@types';
import { $member } from '@stores/memberStores';
import getFileMd5 from '@utils/getFileMd5';
import uploadToQiniu from '@utils/uploadToQiniu';
import { sendError } from '@components/Noti';
import { getAudioLength } from '@utils/getAudioLength';
import { getPDFThumb } from '@utils/getPDFThumb';
import { $libAlbumId } from '../stores/libState';
import { baseSpaceCheck, remainSpaceCheck } from '../actions/spaceCheck';
import libSelectFiles from '../utils/libSelectFiles';
import UploadButton from './UploadButton';
import addToLib from '../actions/addToLib';
import { LIB_SIZE_LIMIT } from '../constants';
import refetchLibSpace from '../utils/refetchLibSpace';

type IApiType =
  | LibApiType.image
  | LibApiType.video
  | LibApiType.audio
  | LibApiType.doc;
type Props = {
  className?: string;
  libApiType: IApiType;
  onUploadDone: () => void;
};
const paramsMap: Record<IApiType, Parameters<typeof libSelectFiles>[0]> = {
  [LibApiType.image]: {
    accept: ['image/png', 'image/gif', 'image/jpeg'],
    sizeLimit: LIB_SIZE_LIMIT[LibType.image],
  },
  [LibApiType.audio]: {
    accept: ['audio/mpeg'],
    sizeLimit: LIB_SIZE_LIMIT[LibType.audio],
  },
  [LibApiType.video]: {
    accept: ['video/mp4'],
    sizeLimit: LIB_SIZE_LIMIT[LibType.video],
  },
  [LibApiType.doc]: {
    accept: ['application/pdf'],
    sizeLimit: LIB_SIZE_LIMIT[LibType.doc],
  },
};

function UploadButtonMedia({ className, libApiType, onUploadDone }: Props) {
  const albumId = useStore($libAlbumId);
  const [itemMap, setMap] = useImmer<LibUploadingMap>(new Map());
  const isMounted = useMountedState();
  const setItemMap: typeof setMap = (arg) => {
    if (isMounted()) {
      setMap(arg);
    }
  };

  const upload = async (file: File, prefix: string) => {
    const id = randomId('libMedia_');
    setItemMap((draft) => {
      draft.set(id, { id, name: file.name, progress: '等待中' });
    });
    try {
      const duration =
        libApiType === LibApiType.audio || libApiType === LibApiType.video
          ? await getAudioLength(file)
          : 0;
      const md5 = await getFileMd5(file);
      const key = `${prefix}/${md5}v5.${getExt(file.name)}`;
      let thumbURL = '';
      if (libApiType === LibApiType.doc) {
        try {
          const thumb = await getPDFThumb(file);
          thumbURL = await uploadToQiniu({
            tokenType: QiniuTokenType.media,
            file: thumb,
            key: `${prefix}/${md5}v5.${nanoid(10)}/thumb.jpg`,
          });
          // eslint-disable-next-line no-empty
        } catch (error) {}
      }
      const url = await uploadToQiniu({
        tokenType: QiniuTokenType.media,
        file,
        key,
        onProgress: (progress) => {
          setItemMap((draft) => {
            const item = draft.get(id);
            if (item) item.progress = progress;
          });
        },
      });
      await addToLib({
        type: libApiType,
        url,
        size: file.size,
        name:
          libApiType === LibApiType.doc || LibApiType.video
            ? removeExt(file.name)
            : file.name,
        duration: Math.ceil(duration),
        albumId,
        ...(libApiType === LibApiType.doc ? { thumb: thumbURL } : {}),
      });
      setItemMap((draft) => {
        draft.delete(id);
      });
      onUploadDone();
    } catch (e) {
      setItemMap((draft) => {
        draft.delete(id);
      });
      if (e.message === 'Request failed with status code 403') {
        sendError('文件格式不正确');
      } else {
        sendError(e.message);
      }
    }
    refetchLibSpace();
  };

  const handleUplpad = async () => {
    await baseSpaceCheck();
    const { files, size } = await libSelectFiles(paramsMap[libApiType]);
    await remainSpaceCheck(size);
    const { uid } = $member.getState().property!;
    const prefix = `/${uid}/${LibApiType[libApiType]}`;
    for (let i = 0; i < files.length; i++) {
      upload(files[i], prefix);
    }
  };

  return (
    <UploadButton
      className={className}
      itemMap={itemMap}
      onClick={handleUplpad}
    />
  );
}

export default UploadButtonMedia;
